<template>
  <HrPageLayout
    title="Approved Recruits"
    subtitle="Employee"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Employee' },
      { label: 'Approved Recruits' }
    ]"
  >
    <div class="employee-approvals-approved">
      <!-- Stats cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Pending Approvals</p>
          <h3 class="hr-metric-value">{{ approvalStats.pending || 0 }}</h3>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Approved Recruits</p>
          <h3 class="hr-metric-value">{{ approvalStats.approved || 0 }}</h3>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-x-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Rejected Applications</p>
          <h3 class="hr-metric-value">{{ approvalStats.rejected || 0 }}</h3>
            </div>
        </div>
      </div>

      <!-- Notification -->
      <div v-if="message" class="hr-toast hr-toast-bottom-right" @click="message = ''">
        <div class="hr-toast-header" :class="message.includes('success') ? 'hr-toast-success' : 'hr-toast-error'">
          <span class="hr-toast-title">{{ message.includes('success') ? 'Success' : 'Error' }}</span>
          <button type="button" class="hr-toast-close" @click.stop="message = ''"><i class="bx bx-x"></i></button>
        </div>
        <div class="hr-toast-body">{{ message }}</div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex hr-justify-between hr-align-center hr-flex-wrap hr-gap-2">
          <div class="hr-search-wrap">
            <div class="hr-search-box">
              <i class="bx bx-search-alt hr-search-icon"></i>
              <input type="text" class="hr-input hr-input-search" placeholder="Search..." v-model="search" @input="handleSearch" />
            </div>
          </div>
          <div class="hr-flex hr-gap-2 hr-flex-wrap hr-align-center">
            <div class="hr-form-inline-group">
              <label class="hr-form-label hr-mb-0">Department</label>
              <select class="hr-select" style="min-width: 200px;" v-model="selectedDepartment" @change="getEmployeeData">
                <option value="">All Departments</option>
                <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.departmentName }}</option>
              </select>
            </div>
            <div class="hr-form-inline-group">
              <label class="hr-form-label hr-mb-0">From</label>
              <input class="hr-input" type="date" v-model="dateFrom" @change="getEmployeeData" />
            </div>
            <div class="hr-form-inline-group">
              <label class="hr-form-label hr-mb-0">To</label>
              <input class="hr-input" type="date" v-model="dateTo" @change="getEmployeeData" />
            </div>
          </div>
        </div>

        <div class="hr-chart-body">
          <div v-if="isLoading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading approved recruits...</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover hr-table-bordered hr-table-sm">
              <thead>
                <tr>
                  <th>App No.</th>
                  <th>Name</th>
                  <th>ID No.</th>
                  <th>Contract Type</th>
                  <th>Proposed Salary</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th>Date Approved</th>
                  <th>Submitted By</th>
                  <th class="hr-text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in employeeData" :key="item.id || index">
                  <td>{{ item.id }}</td>
                  <td>
                    <button class="hr-link-btn" @click="gotTo(item.id, 'employee-approvals-new-verification')">
                      {{ item.firstName }} {{ item.lastName }}
                    </button>
                  </td>
                  <td>{{ item.nationalId }}</td>
                  <td>{{ item.contractType }}</td>
                  <td>KES {{ item.salary }}</td>
                  <td>{{ item.departmentName || item.department || 'N/A' }}</td>
                  <td>{{ item.designation }}</td>
                  <td>{{ item.dateCreated }}</td>
                  <td>{{ item.createdBy }}</td>
                  <td class="hr-text-right">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="gotTo(item.id, 'employee-approvals-new-verification')">
                      <i class="bx bx-show"></i> View
                    </button>
                  </td>
                </tr>
                <tr v-if="!isLoading && employeeData.length === 0">
                  <td colspan="10" class="hr-text-center hr-text-muted">No approved recruits found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

export default {
  name: "EmployeeApprovalsApproved",
  components: { HrPageLayout },
  data() {
    return {
      search: "",
      dateFrom: "",
      dateTo: "",
      selectedDepartment: "",
      departments: [],
      message: "",
      isLoading: false,
      approvalStats: {
        pending: 0,
        approved: 0,
        rejected: 0
      },
      employeeData: []
    };
  },
  mounted() {
    this.getDepartments();
    this.getEmployeeData();
    this.getApprovalStats();
  },
  methods: {
    getRight(type) {
      return getRight(type);
    },
    gotTo(employeeDataId, route) {
      sessionStorage.setItem("employeeDataId", employeeDataId);
      this.$router.push({ name: route });
    },
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data && Array.isArray(res.data.data.departments)) {
            this.departments = res.data.data.departments;
          }
        })
        .catch(() => {});
    },
    getApprovalStats() {
      const data = new FormData();
      data.append("function", "getEmployeeApprovalStats");
      execute(data, authUrl)
        .then((res) => {
          if (res.data && res.data.success && res.data.data && res.data.data.stats) {
            this.approvalStats = {
              pending: Number(res.data.data.stats.pending) || 0,
              approved: Number(res.data.data.stats.approved) || 0,
              rejected: Number(res.data.data.stats.rejected) || 0
            };
          }
        })
        .catch(() => {});
    },
    handleSearch() {
      this.getEmployeeData();
    },
    getEmployeeData() {
      this.isLoading = true;
      const data = new FormData();
      data.append("function", "getEmployeeData");
      data.append("approved", "true");
      if (this.selectedDepartment) data.append("department", this.selectedDepartment);
      if (this.search) data.append("keyword", this.search);
      if (this.dateFrom) data.append("dateFrom", this.dateFrom);
      if (this.dateTo) data.append("dateTo", this.dateTo);

      execute(data, authUrl)
        .then((res) => {
          this.isLoading = false;
          if (res.data && res.data.success && res.data.data && res.data.data.employeeData) {
            this.employeeData = (res.data.data.employeeData || []).map((item) => {
              let parsedData = {};
              try {
                parsedData = item.data ? JSON.parse(item.data) : {};
              } catch (e) {
                parsedData = {};
              }
              const emp = parsedData.employeeData || {};
              const extra = parsedData.extraDetails || {};
              const employment = parsedData.employmentDetails || {};
              const bank = parsedData.bankDetails || {};
              return {
                id: item.id,
                firstName: emp.firstName || "N/A",
                lastName: emp.lastName || "N/A",
                nationalId: emp.nationalId || "N/A",
                contractType: extra.employmentType || "N/A",
                salary: bank.netSalary || "0",
                department: employment.department || "N/A",
                departmentName: item.departmentName || null,
                designation: employment.designation || "N/A",
                dateCreated: item.dateCreated || "N/A",
                createdBy: item.createdBy || "N/A"
              };
            });
          } else {
            this.employeeData = [];
            if (res.data && res.data.message) this.message = res.data.message;
          }
        })
        .catch((e) => {
          this.isLoading = false;
          this.employeeData = [];
          this.message = e.message || "Failed to load approved recruits";
        });
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.hr-mb-4 {
  margin-bottom: 1.5rem;
}
.hr-search-wrap {
  min-width: 220px;
}
.hr-search-box {
  position: relative;
}
.hr-input-search {
  padding-left: 34px;
}
.hr-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
}
.hr-form-inline-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.hr-link-btn {
  border: none;
  background: none;
  padding: 0;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.9rem;
}
.hr-link-btn:hover {
  color: #1d4ed8;
}
.hr-loading-wrap {
  padding: 2rem;
  text-align: center;
}
.hr-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: hr-spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}
@keyframes hr-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
